import { ThunkAction } from 'redux-thunk';

import { AppAction, AppState } from 'types/app';
import {
  CANCEL_ORDER_HISTORY_SEARCH,
  ERROR_ORDER_HISTORY_SEARCH,
  RECEIVE_ORDER_HISTORY_SEARCH,
  RECEIVE_ORDERS,
  REQUEST_ORDER_HISTORY_SEARCH,
  REQUEST_ORDERS,
  TOGGLE_ORDER_COLLAPSE
} from 'constants/reduxActions';
import { prependAppRoot } from 'history/AppRootUtils';
import { authenticationErrorCatchHandlerFactory, fetchOrderHistorySearchCatchHandler } from 'actions/errors';
import { fetchApiNotAuthenticatedMiddleware, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { getOrdersBySearch, getOrdersV3 } from 'apis/mafia';
import { OrderQueryParams, OrdersResponse } from 'types/mafia';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';

export type OrdersAction =
  | ReturnType<typeof requestOrders>
  | ReturnType<typeof receiveOrders>
  | ReturnType<typeof cancelOrderHistorySearch>
  | ReturnType<typeof errorOrderHistorySearch>
  | ReturnType<typeof requestOrderHistorySearch>
  | ReturnType<typeof receiveOrderHistorySearch>
  | ReturnType<typeof toggleOrderCollapse>;

export function requestOrders() {
  return {
    type: REQUEST_ORDERS
  } as const;
}

export function receiveOrders(resp: OrdersResponse) {
  const { orders, totalPages } = resp;
  return {
    type: RECEIVE_ORDERS,
    orders: orders || [],
    totalPages
  } as const;
}

export function requestOrderHistorySearch() {
  return {
    type: REQUEST_ORDER_HISTORY_SEARCH
  } as const;
}

export function receiveOrderHistorySearch(resp: OrdersResponse) {
  const { orders, totalPages } = resp;
  return {
    type: RECEIVE_ORDER_HISTORY_SEARCH,
    orders: orders || [],
    totalPages
  } as const;
}

export function cancelOrderHistorySearch() {
  return {
    type: CANCEL_ORDER_HISTORY_SEARCH
  } as const;
}

export function errorOrderHistorySearch() {
  return {
    type: ERROR_ORDER_HISTORY_SEARCH
  } as const;
}

export function toggleOrderCollapse(orderIdToCollapse: string) {
  return {
    type: TOGGLE_ORDER_COLLAPSE,
    orderIdToCollapse
  } as const;
}

interface FetchOrdersOptions {
  returnTo?: string;
  failSilently?: boolean;
}

export function fetchOrderHistorySearch(
  queryParams: Partial<OrderQueryParams>,
  { returnTo = '/account' }
): ThunkAction<void, AppState, void, AppAction> {
  return (dispatch, getState) => {
    dispatch(requestOrderHistorySearch());
    const {
      cookies,
      environmentConfig: {
        api: { mafia }
      },
      router: { location }
    } = getState();
    return getOrdersBySearch(mafia, queryParams, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(receiveOrderHistorySearch(resp));
      })
      .catch(fetchOrderHistorySearchCatchHandler(dispatch, prependAppRoot(returnTo, location), errorOrderHistorySearch));
  };
}

export function fetchOrders(
  queryParams: Partial<OrderQueryParams>,
  { returnTo = '/account', failSilently = false }: FetchOrdersOptions = {}
): ThunkAction<void, AppState, void, AppAction> {
  return (dispatch, getState) => {
    dispatch(requestOrders());
    const {
      cookies,
      environmentConfig: {
        api: {
          mafia: { url }
        }
      },
      router: { location }
    } = getState();
    return (
      getOrdersV3({ url }, queryParams, cookies)
        .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
        .then(fetchApiNotAuthenticatedMiddleware)
        .then(fetchErrorMiddleware)
        .then(resp => {
          dispatch(receiveOrders(resp));
        })
        // just ignore any error thrown above if silent fail flag passed
        .catch(e =>
          !failSilently
            ? authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(returnTo, location))(e)
            : dispatch(receiveOrders({ orders: [], totalPages: 0 }))
        )
    );
  };
}
